import {
  OAuthProviderType,
  ServerDeploymentType,
  ServerFeature,
} from '@affine/graphql';

import type { ServerConfig, ServerMetadata } from './types';

const HAPPENED_WORLD = 'https://happened.world';
const nativeCloudBaseUrl = BUILD_CONFIG.isNative ? HAPPENED_WORLD : location.origin;

function createCloudServer(
  baseUrl: string
): ServerMetadata & { config: ServerConfig } {
  return {
    id: 'affine-cloud',
    baseUrl,
    config: {
      serverName: 'Happened Cloud',
      features: [
        ServerFeature.Indexer,
        ServerFeature.Copilot,
        ServerFeature.CopilotEmbedding,
        ServerFeature.OAuth,
        ServerFeature.Payment,
        ServerFeature.LocalWorkspace,
      ],
      oauthProviders: [OAuthProviderType.Google, OAuthProviderType.Apple],
      type: ServerDeploymentType.Affine,
      credentialsRequirement: {
        password: {
          minLength: 8,
          maxLength: 32,
        },
      },
    },
  };
}

export const BUILD_IN_SERVERS: (ServerMetadata & { config: ServerConfig })[] =
  environment.isSelfHosted
    ? [
        {
          id: 'affine-cloud',
          baseUrl: location.origin,
          // selfhosted baseUrl is `location.origin`
          // this is ok for web app, but not for desktop app
          // since we never build desktop app in selfhosted mode, so it's fine
          config: {
            serverName: 'Happened Self-host',
            features: [],
            oauthProviders: [],
            type: ServerDeploymentType.Selfhosted,
            credentialsRequirement: {
              password: {
                minLength: 8,
                maxLength: 32,
              },
            },
          },
        },
      ]
    : BUILD_CONFIG.debug
      ? [
          createCloudServer(
            BUILD_CONFIG.isElectron ? 'http://localhost:8080' : location.origin
          ),
        ]
      : BUILD_CONFIG.appBuildType === 'stable'
        ? [createCloudServer(nativeCloudBaseUrl)]
        : BUILD_CONFIG.appBuildType === 'beta'
          ? [createCloudServer(nativeCloudBaseUrl)]
          : BUILD_CONFIG.appBuildType === 'internal'
            ? [createCloudServer(HAPPENED_WORLD)]
            : BUILD_CONFIG.appBuildType === 'canary'
              ? [createCloudServer(nativeCloudBaseUrl)]
              : [];

export type TelemetryChannel =
  | 'stable'
  | 'beta'
  | 'internal'
  | 'canary'
  | 'local';

const OFFICIAL_TELEMETRY_ENDPOINTS: Record<TelemetryChannel, string> = {
  stable: HAPPENED_WORLD,
  beta: HAPPENED_WORLD,
  internal: HAPPENED_WORLD,
  canary: HAPPENED_WORLD,
  local: 'http://localhost:8080',
};

export function getOfficialTelemetryEndpoint(
  channel = BUILD_CONFIG.appBuildType
): string {
  if (BUILD_CONFIG.debug) {
    return BUILD_CONFIG.isNative
      ? OFFICIAL_TELEMETRY_ENDPOINTS.local
      : location.origin;
  } else if (['beta', 'internal', 'canary', 'stable'].includes(channel)) {
    return OFFICIAL_TELEMETRY_ENDPOINTS[channel];
  }

  return OFFICIAL_TELEMETRY_ENDPOINTS.stable;
}
